import { createElement, useRef, useState } from 'react';

type AutoFocusHandler = (event: Event) => void;
type OutsideHandler<E> = (event: E) => void;

/**
 * Reads the focused element while the overlay's content first renders. Rendering happens
 * before React commits, so this runs before an `autoFocus` field inside the overlay takes
 * focus. Radix's own open event is skipped in that case (focus is already inside), which is
 * why `onOpenAutoFocus` alone missed overlays with an autofocused field.
 */
const OpenerCapture = ({ onCapture }: { onCapture: (opener: HTMLElement | null) => void }) => {
  useState(() => {
    onCapture(document.activeElement instanceof HTMLElement ? document.activeElement : null);
    return null;
  });
  return null;
};

/**
 * Radix overlays return focus only to their own `Trigger`. Most overlays here are opened
 * from plain buttons, anchors or keyboard shortcuts, so focus fell to `<body>` on close.
 * This remembers whatever had focus when the overlay opened and hands focus back to it.
 * Render `capture` as the first child of the overlay content.
 *
 * Non-modal overlays (popovers) close when you click elsewhere; pass `skipAfterOutside`
 * so that click keeps its focus instead of being pulled back to the opener.
 */
export const useReturnFocus = <E = Event>({
  onOpenAutoFocus,
  onCloseAutoFocus,
  onInteractOutside,
  skipAfterOutside = false,
}: {
  onOpenAutoFocus?: AutoFocusHandler;
  onCloseAutoFocus?: AutoFocusHandler;
  onInteractOutside?: OutsideHandler<E>;
  skipAfterOutside?: boolean;
}) => {
  const openerRef = useRef<HTMLElement | null>(null);
  const interactedOutsideRef = useRef(false);

  const remember = (element: Element | null) => {
    // The first capture wins: by the open event focus may already be inside the overlay.
    if (!openerRef.current && element instanceof HTMLElement && element !== document.body) {
      openerRef.current = element;
    }
  };

  return {
    capture: createElement(OpenerCapture, { onCapture: remember }),
    handlers: {
      onOpenAutoFocus: (event: Event) => {
        remember(document.activeElement);
        interactedOutsideRef.current = false;
        onOpenAutoFocus?.(event);
      },
      onCloseAutoFocus: (event: Event) => {
        onCloseAutoFocus?.(event);
        const opener = openerRef.current;
        const interactedOutside = interactedOutsideRef.current;
        openerRef.current = null;
        interactedOutsideRef.current = false;
        if (event.defaultPrevented || !opener?.isConnected) {
          return;
        }
        event.preventDefault();
        if (!(skipAfterOutside && interactedOutside)) {
          opener.focus();
        }
      },
      onInteractOutside: (event: E) => {
        onInteractOutside?.(event);
        if (!(event as { defaultPrevented?: boolean }).defaultPrevented) {
          interactedOutsideRef.current = true;
        }
      },
    },
  };
};
